import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertTriangle } from "lucide-react";

export default function Disclaimer() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#030712] relative">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />

      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button 
            data-testid="back-btn"
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-10 h-10 text-[#F59E0B]" />
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
              DISCLAIMER
            </h1>
          </div>
          <p className="text-gray-400">Important Information About Trading Risks</p>
        </motion.div>

        <Card className="glass-effect border-[#F59E0B]/30">
          <CardContent className="p-8 space-y-6 text-gray-300">
            <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-[#F59E0B] mb-3" style={{ fontFamily: 'Unbounded' }}>
                ⚠️ IMPORTANT RISK WARNING
              </h3>
              <p className="leading-relaxed text-white">
                Trading stocks and cryptocurrencies involves substantial risk of loss and is not suitable for all investors. You should carefully consider whether trading is appropriate for you in light of your experience, objectives, financial resources, and other relevant circumstances.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>NOT FINANCIAL ADVICE</h2>
              <p className="leading-relaxed mb-3">
                The information provided by TradeInsights, including AI-generated predictions, market analysis, and educational content, is for <strong>informational and educational purposes only</strong>. It does NOT constitute:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Financial advice</li>
                <li>Investment recommendations</li>
                <li>Legal advice</li>
                <li>Tax advice</li>
                <li>Professional consultation of any kind</li>
              </ul>
              <p className="leading-relaxed mt-3">
                You should NOT rely on this information as a substitute for consultation with professional financial, legal, or tax advisors.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>AI PREDICTIONS LIMITATIONS</h2>
              <p className="leading-relaxed mb-3">
                Our AI-powered predictions:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Are based on historical data and algorithmic analysis</li>
                <li>Cannot guarantee future market performance</li>
                <li>May contain errors or inaccuracies</li>
                <li>Should not be the sole basis for investment decisions</li>
                <li>Do not account for individual financial situations</li>
                <li>Are subject to market volatility and unpredictable events</li>
              </ul>
              <p className="leading-relaxed mt-3 font-semibold text-white">
                Past performance is NOT indicative of future results.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>INVESTMENT RISKS</h2>
              <p className="leading-relaxed mb-3">
                All investments carry risk, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Market Risk:</strong> Prices can go up or down based on market conditions</li>
                <li><strong>Volatility Risk:</strong> Especially high in cryptocurrency markets</li>
                <li><strong>Liquidity Risk:</strong> You may not be able to sell when desired</li>
                <li><strong>Regulatory Risk:</strong> Changes in laws may affect investments</li>
                <li><strong>Technology Risk:</strong> Platform or network failures</li>
                <li><strong>Total Loss:</strong> You may lose your entire investment</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>CRYPTOCURRENCY SPECIFIC RISKS</h2>
              <p className="leading-relaxed mb-3">
                Cryptocurrencies are particularly volatile and risky:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Extreme price volatility (can lose 50%+ in days)</li>
                <li>Limited regulatory oversight</li>
                <li>Risk of theft, hacking, or loss of private keys</li>
                <li>May become worthless if project fails</li>
                <li>Highly speculative nature</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>YOUR RESPONSIBILITY</h2>
              <p className="leading-relaxed mb-3">
                By using TradeInsights, you acknowledge that:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You are solely responsible for your investment decisions</li>
                <li>You should conduct your own research and due diligence</li>
                <li>You should consult qualified financial professionals</li>
                <li>You understand the risks involved in trading</li>
                <li>You will not hold TradeInsights liable for trading losses</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>NO GUARANTEES</h2>
              <p className="leading-relaxed">
                We make no warranties or guarantees about:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
                <li>Accuracy of predictions or market data</li>
                <li>Profitability of any investment</li>
                <li>Uninterrupted service availability</li>
                <li>Suitability for your specific situation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>DATA SOURCES</h2>
              <p className="leading-relaxed">
                Market data is sourced from third-party providers (Alpha Vantage, CoinGecko). We are not responsible for the accuracy, completeness, or timeliness of this data. Delays or errors in data may occur.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>EDUCATIONAL PURPOSE</h2>
              <p className="leading-relaxed">
                The learning materials and educational content are designed to help you understand trading concepts. They do not guarantee trading success and should be supplemented with professional education and practice.
              </p>
            </section>

            <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-lg p-6 mt-8">
              <h3 className="text-xl font-bold text-[#FF3B30] mb-3" style={{ fontFamily: 'Unbounded' }}>
                ⚠️ FINAL WARNING
              </h3>
              <p className="leading-relaxed text-white font-semibold">
                ONLY INVEST MONEY YOU CAN AFFORD TO LOSE. Never invest borrowed money or funds needed for essential expenses.
              </p>
            </div>

            <section className="mt-6">
              <p className="leading-relaxed text-sm text-gray-400">
                By continuing to use TradeInsights, you acknowledge that you have read, understood, and agreed to this Disclaimer.
              </p>
            </section>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
